import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Phone, ArrowLeft, Loader2, CheckCircle, Lock, User, Mail, Eye, EyeOff, Stethoscope, Heart, UserCircle, Building } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { analytics } from '../lib/analytics';
import { apiRequest } from '../lib/utils';
import { toast } from 'sonner';

const PHYSIGN_LOGO = "https://customer-assets.emergentagent.com/job_4bf5f59f-ff2a-4b89-ab5f-09ffb3b85c36/artifacts/91dqnx5f_high-resolution-color-logo-3.png";

export default function Auth() {
  const navigate = useNavigate();
  const { login, isAuthenticated, user, logout, checkAuth } = useAuth();

  const [loading, setLoading] = useState(false);
  const [showLogoutOption, setShowLogoutOption] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const [showPassword, setShowPassword] = useState(false);

  // Login states (Phone + OTP for everyone)
  const [loginPhone, setLoginPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpStep, setOtpStep] = useState('phone');
  const [demoOtp, setDemoOtp] = useState('');
  const [authMethod, setAuthMethod] = useState('mock');
  const [resendTimer, setResendTimer] = useState(0);
  const [loginMode, setLoginMode] = useState('otp'); // otp or password
  const [loginPassword, setLoginPassword] = useState('');
  // Email login
  const [loginIdentifier, setLoginIdentifier] = useState('phone'); // 'phone' or 'email'
  const [loginEmail, setLoginEmail] = useState('');

  // Register states
  const [regCategory, setRegCategory] = useState(''); // '' or 'patient' or 'provider'
  const [regRole, setRegRole] = useState('');
  const [regName, setRegName] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');

  useEffect(() => {
    if (isAuthenticated && user) setShowLogoutOption(true);
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (resendTimer > 0) {
      const t = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendTimer]);

  const validatePhone = (num) => {
    const clean = num.replace(/\D/g, '');
    return clean.length === 10 && /^[6-9]/.test(clean);
  };

  const validateEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || '').trim());

  const redirectToDashboard = (userRole) => {
    if (userRole === 'admin') navigate('/physign-admin-panel');
    else if (userRole === 'patient') navigate('/dashboard');
    else if (userRole === 'clinic') navigate('/clinic');
    else navigate('/provider');
  };

  // ====== LOGIN: Send OTP ======
  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!validatePhone(loginPhone)) { toast.error('Valid 10-digit phone number enter karein'); return; }
    setLoading(true);
    try {
      const data = await apiRequest('/auth/send-otp', {
        method: 'POST',
        body: JSON.stringify({ phone: loginPhone }),
      });
      setDemoOtp(data.demo_otp || '');
      setAuthMethod(data.method || 'mock');
      setOtpStep('otp');
      setResendTimer(30);
      const successMsg = data.method === 'whatsapp'
        ? 'OTP sent on your WhatsApp!'
        : (data.method === 'msg91' || data.method === 'twilio')
        ? 'OTP sent via SMS!'
        : 'OTP sent — please check your phone';
      toast.success(successMsg);
      analytics.otpRequested(data.method || 'unknown');
    } catch (error) { toast.error(error.message); }
    finally { setLoading(false); }
  };

  // ====== LOGIN: Verify OTP ======
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { toast.error('6-digit OTP enter karein'); return; }
    setLoading(true);
    try {
      const data = await login(loginPhone, otp);
      analytics.loginCompleted('otp');
      toast.success('Login successful!');
      await redirectProviderAware(data.user?.role || 'patient');
    } catch (error) {
      if (error.message?.includes('not registered')) {
        toast.error('Phone number registered nahi hai. Pehle Register karein.');
      } else {
        toast.error(error.message);
      }
    }
    finally { setLoading(false); }
  };

  // ====== LOGIN: Password Login ======
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!validatePhone(loginPhone)) { toast.error('Valid phone number enter karein'); return; }
    if (!loginPassword) { toast.error('Password enter karein'); return; }
    setLoading(true);
    try {
      const data = await apiRequest('/auth/password-login', {
        method: 'POST',
        body: JSON.stringify({ phone: loginPhone, password: loginPassword }),
      });
      localStorage.setItem('token', data.token);
      // Sync AuthContext so ProtectedRoute sees authenticated state
      await checkAuth();
      analytics.loginCompleted('password');
      toast.success('Login successful!');
      await redirectProviderAware(data.user?.role || 'patient');
    } catch (error) {
      if (error.message?.includes('Password not set')) {
        toast.error('Password set nahi hai. OTP se login karein.');
        setLoginMode('otp');
      } else {
        toast.error(error.message);
      }
    }
    finally { setLoading(false); }
  };

  // ====== LOGIN: Email + Password Login ======
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!validateEmail(loginEmail)) { toast.error('Valid email enter karein'); return; }
    if (!loginPassword) { toast.error('Password enter karein'); return; }
    setLoading(true);
    try {
      const data = await apiRequest('/auth/password-login', {
        method: 'POST',
        body: JSON.stringify({ email: loginEmail.trim().toLowerCase(), password: loginPassword }),
      });
      localStorage.setItem('token', data.token);
      await checkAuth();
      analytics.loginCompleted('email_password');
      toast.success('Login successful!');
      await redirectProviderAware(data.user?.role || 'patient');
    } catch (error) {
      if (error.message?.includes('Password not set')) {
        toast.error('Password set nahi hai. OTP se login karein.');
        setLoginIdentifier('phone');
        setLoginMode('otp');
      } else {
        toast.error(error.message);
      }
    }
    finally { setLoading(false); }
  };

  // For doctor/nurse, check onboarding_completed and route accordingly
  const redirectProviderAware = async (userRole) => {
    if (userRole === 'doctor' || userRole === 'nurse') {
      try {
        const me = await apiRequest('/auth/me');
        if (me?.profile?.onboarding_completed !== true) {
          navigate('/provider/onboarding');
          return;
        }
      } catch { /* fall through to default redirect */ }
      navigate('/provider');
      return;
    }
    redirectToDashboard(userRole);
  };

  // ====== REGISTER ======
  const handleRegister = async (e) => {
    e.preventDefault();
    if (!regRole) { toast.error('Role select karein'); return; }
    if (!regName.trim()) { toast.error('Name enter karein'); return; }
    if (!validatePhone(regPhone)) { toast.error('Valid 10-digit phone number enter karein'); return; }
    if (!regPassword || regPassword.length < 6) { toast.error('Password minimum 6 characters hona chahiye'); return; }
    if ((regRole === 'doctor' || regRole === 'nurse') && !regEmail) { toast.error('Email enter karein'); return; }
    setLoading(true);
    try {
      const data = await apiRequest('/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          name: regName,
          phone: regPhone,
          email: regEmail || null,
          password: regPassword,
          role: regRole,
        }),
      });
      localStorage.setItem('token', data.token);
      analytics.signupCompleted(regRole);
      toast.success('Registration successful!');
      const role = data.user?.role || 'patient';
      // New doctor/nurse signups → onboarding wizard
      const target = role === 'patient' ? '/dashboard'
        : role === 'clinic' ? '/clinic'
        : (role === 'doctor' || role === 'nurse') ? '/provider/onboarding'
        : '/provider';
      window.location.href = target;
    } catch (error) { toast.error(error.message); }
    finally { setLoading(false); }
  };

  // Already logged in
  if (showLogoutOption && isAuthenticated && user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardContent className="p-6 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-[#E0F2FE] flex items-center justify-center mx-auto text-xl font-bold text-[#0F62FE]">
              {user.name?.[0]?.toUpperCase() || 'U'}
            </div>
            <h2 className="text-lg font-bold text-slate-900">{user.name || 'User'}</h2>
            <p className="text-sm text-slate-500 capitalize">{user.role}</p>
            <Button className="w-full bg-[#0F62FE] hover:bg-[#0050E6]" onClick={() => redirectProviderAware(user.role)}>
              Continue as {user.name || 'User'}
            </Button>
            <Button variant="outline" className="w-full" onClick={() => { logout(); setShowLogoutOption(false); }}>
              Switch Account
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const roleStyleMap = {
    patient: { icon: UserCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    doctor: { icon: Stethoscope, color: 'text-blue-600', bg: 'bg-blue-50' },
    nurse: { icon: Heart, color: 'text-rose-600', bg: 'bg-rose-50' },
    clinic: { icon: Building, color: 'text-purple-600', bg: 'bg-purple-50' },
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="auth-page">
      {/* Header */}
      <header className="py-4 px-6 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-xl font-bold text-[#3B1C73]" style={{ fontFamily: 'Plus Jakarta Sans' }}>Physign</span>
          </Link>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center p-4 py-8">
        <Card className="w-full max-w-md shadow-lg border-slate-100" data-testid="auth-card">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-3">
              <div className="w-14 h-14 bg-[#0F62FE] rounded-2xl flex items-center justify-center">
                <Phone className="w-7 h-7 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Welcome to Physign
            </CardTitle>
            <CardDescription>Login or create your account</CardDescription>
          </CardHeader>

          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid grid-cols-2 w-full mb-4">
                <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
              </TabsList>

              {/* ========== LOGIN TAB - Phone + OTP OR Email + Password ========== */}
              <TabsContent value="login">
                {/* Login method toggle: Phone vs Email (Email hidden for now) */}
                {false && (
                <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-lg mb-4" data-testid="login-method-toggle">
                  <button
                    type="button"
                    onClick={() => { setLoginIdentifier('phone'); setOtpStep('phone'); setLoginMode('otp'); }}
                    className={`flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-all ${
                      loginIdentifier === 'phone' ? 'bg-white shadow-sm text-[#0F62FE]' : 'text-slate-500 hover:text-slate-700'
                    }`}
                    data-testid="login-method-phone"
                  >
                    <Phone className="w-3.5 h-3.5" />Phone
                  </button>
                  <button
                    type="button"
                    onClick={() => { setLoginIdentifier('email'); setOtpStep('phone'); setLoginMode('otp'); }}
                    className={`flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-all ${
                      loginIdentifier === 'email' ? 'bg-white shadow-sm text-[#0F62FE]' : 'text-slate-500 hover:text-slate-700'
                    }`}
                    data-testid="login-method-email"
                  >
                    <Mail className="w-3.5 h-3.5" />Email
                  </button>
                </div>
                )}

                {loginIdentifier === 'email' ? (
                  /* Email + Password Login Form */
                  <form onSubmit={handleEmailLogin} className="space-y-4" data-testid="login-email-form">
                    <div>
                      <Label>Email Address</Label>
                      <div className="relative mt-1">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          type="email"
                          placeholder="your@email.com"
                          value={loginEmail}
                          onChange={(e) => setLoginEmail(e.target.value)}
                          className={`pl-10 ${loginEmail && !validateEmail(loginEmail) ? 'border-red-400' : ''}`}
                          autoComplete="email"
                          data-testid="login-email-input"
                          required
                        />
                      </div>
                      {loginEmail && !validateEmail(loginEmail) && (
                        <p className="text-xs text-red-500 mt-1">Valid email enter karein</p>
                      )}
                    </div>
                    <div>
                      <Label>Password</Label>
                      <div className="relative mt-1">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter your password"
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          className="pl-10 pr-10"
                          autoComplete="current-password"
                          data-testid="login-email-password-input"
                          required
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <Button
                      type="submit"
                      className="w-full h-11 bg-[#0F62FE] hover:bg-[#0050E6]"
                      disabled={loading || !validateEmail(loginEmail) || !loginPassword}
                      data-testid="email-login-btn"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}Login with Email
                    </Button>
                    <p className="text-center text-sm">
                      <Link
                        to="/auth/forgot-password"
                        className="text-[#0F62FE] hover:underline font-medium"
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </Link>
                    </p>
                  </form>
                ) : otpStep === 'phone' ? (
                  <form onSubmit={handleSendOtp} className="space-y-4" data-testid="login-phone-form">
                    <div>
                      <Label>Phone Number</Label>
                      <div className="flex gap-2 mt-1">
                        <div className="w-16 h-10 px-2 border border-slate-200 rounded-lg bg-slate-50 flex items-center justify-center text-sm font-medium text-slate-700">+91</div>
                        <Input
                          type="tel"
                          placeholder="10-digit phone number"
                          value={loginPhone}
                          onChange={(e) => setLoginPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                          className={`flex-1 ${loginPhone && !validatePhone(loginPhone) ? 'border-red-400' : ''}`}
                          maxLength={10}
                          data-testid="login-phone-input"
                        />
                      </div>
                      {loginPhone && !validatePhone(loginPhone) && (
                        <p className="text-xs text-red-500 mt-1">Valid 10-digit number enter karein (6/7/8/9 se start)</p>
                      )}
                    </div>
                    <Button type="submit" className="w-full h-11 bg-[#0F62FE] hover:bg-[#0050E6]" disabled={loading || !validatePhone(loginPhone)} data-testid="send-otp-btn">
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Phone className="w-4 h-4 mr-2" />}Send OTP
                    </Button>
                  </form>
                ) : loginMode === 'otp' ? (
                  <form onSubmit={handleVerifyOtp} className="space-y-4" data-testid="login-otp-form">
                    <button type="button" onClick={() => { setOtpStep('phone'); setOtp(''); setLoginMode('otp'); }} className="flex items-center text-sm text-slate-600 hover:text-slate-900">
                      <ArrowLeft className="w-4 h-4 mr-1" />Change number
                    </button>
                    <div>
                      <Label>Enter 6-digit OTP</Label>
                      <div className="flex justify-center mt-2">
                        <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
                          <InputOTPGroup>
                            <InputOTPSlot index={0} /><InputOTPSlot index={1} /><InputOTPSlot index={2} />
                            <InputOTPSlot index={3} /><InputOTPSlot index={4} /><InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                      </div>
                    </div>
                    {(authMethod === 'whatsapp') ? (
                      <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                        <p className="text-sm text-emerald-700 text-center">OTP sent on your WhatsApp</p>
                      </div>
                    ) : (authMethod === 'msg91' || authMethod === 'twilio') ? (
                      <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                        <p className="text-sm text-emerald-700 text-center">OTP sent via SMS</p>
                      </div>
                    ) : null}
                    <Button type="submit" className="w-full h-11 bg-[#0F62FE] hover:bg-[#0050E6]" disabled={loading || otp.length !== 6} data-testid="verify-otp-btn">
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}Verify & Login
                    </Button>
                    <p className="text-center text-sm text-slate-500">
                      Didn't receive?{' '}
                      {resendTimer > 0 ? (
                        <span className="text-slate-400 font-medium" data-testid="resend-timer">Resend in {resendTimer}s</span>
                      ) : (
                        <button type="button" onClick={handleSendOtp} className="text-[#0F62FE] hover:underline font-medium" data-testid="resend-otp-btn">Resend OTP</button>
                      )}
                    </p>
                    <div className="relative my-2">
                      <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200" /></div>
                      <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-slate-400">or</span></div>
                    </div>
                    <button type="button" onClick={() => setLoginMode('password')} className="w-full text-center text-sm text-[#0F62FE] hover:underline font-medium" data-testid="switch-to-password">
                      <Lock className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />Password se login karein
                    </button>
                  </form>
                ) : (
                  /* Password Login Form */
                  <form onSubmit={handlePasswordLogin} className="space-y-4" data-testid="login-password-form">
                    <button type="button" onClick={() => setLoginMode('otp')} className="flex items-center text-sm text-slate-600 hover:text-slate-900">
                      <ArrowLeft className="w-4 h-4 mr-1" />Back to OTP
                    </button>
                    <div className="flex items-center gap-2 p-2.5 rounded-lg bg-slate-50">
                      <Phone className="w-4 h-4 text-slate-500" />
                      <span className="text-sm font-medium text-slate-700">+91 {loginPhone}</span>
                    </div>
                    <div>
                      <Label>Password</Label>
                      <div className="relative mt-1">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input type={showPassword ? 'text' : 'password'} placeholder="Enter your password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} className="pl-10 pr-10" data-testid="login-password-input" required />
                        <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <Button type="submit" className="w-full h-11 bg-[#0F62FE] hover:bg-[#0050E6]" disabled={loading || !loginPassword} data-testid="password-login-btn">
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}Login with Password
                    </Button>
                  </form>
                )}

                <p className="text-center text-sm text-slate-500 mt-4">
                  Don't have an account?{' '}
                  <button type="button" className="text-[#0F62FE] hover:underline font-medium" onClick={() => setActiveTab('register')}>Register</button>
                </p>
              </TabsContent>

              {/* ========== REGISTER TAB - Role Selection + Form ========== */}
              <TabsContent value="register">
                {/* Step 1: Patient or Provider */}
                {!regCategory ? (
                  <div className="space-y-3" data-testid="role-selection">
                    <p className="text-sm text-slate-600 font-medium text-center mb-2">Register as:</p>
                    <button
                      type="button"
                      className="w-full flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all hover:shadow-sm bg-emerald-50 border-slate-200 hover:border-emerald-500"
                      onClick={() => { setRegCategory('patient'); setRegRole('patient'); }}
                      data-testid="reg-role-patient"
                    >
                      <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                        <UserCircle className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-slate-900">Patient</p>
                        <p className="text-xs text-slate-500">Book appointments & manage health</p>
                      </div>
                    </button>
                    <button
                      type="button"
                      className="w-full flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all hover:shadow-sm bg-blue-50 border-slate-200 hover:border-blue-500"
                      onClick={() => setRegCategory('provider')}
                      data-testid="reg-role-provider"
                    >
                      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                        <Stethoscope className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-slate-900">Provider</p>
                        <p className="text-xs text-slate-500">Doctor or Nurse - manage patients</p>
                      </div>
                    </button>
                    {/* Clinic/Agency option - hidden */}
                    <p className="text-center text-sm text-slate-500 mt-3">
                      Already have an account?{' '}
                      <button type="button" className="text-[#0F62FE] hover:underline font-medium" onClick={() => setActiveTab('login')}>Login</button>
                    </p>
                  </div>
                ) : regCategory === 'provider' && !regRole ? (
                  /* Step 2: Doctor or Nurse sub-selection */
                  <div className="space-y-3" data-testid="provider-type-selection">
                    <button type="button" onClick={() => setRegCategory('')} className="flex items-center text-sm text-slate-500 hover:text-slate-700">
                      <ArrowLeft className="w-4 h-4 mr-1" />Back
                    </button>
                    <p className="text-sm text-slate-600 font-medium text-center mb-2">Register as:</p>
                    <button
                      type="button"
                      className="w-full flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all hover:shadow-sm bg-blue-50 border-slate-200 hover:border-blue-500"
                      onClick={() => setRegRole('doctor')}
                      data-testid="reg-role-doctor"
                    >
                      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                        <Stethoscope className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-slate-900">Doctor</p>
                        <p className="text-xs text-slate-500">Manage patients & schedule</p>
                      </div>
                    </button>
                    <button
                      type="button"
                      className="w-full flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all hover:shadow-sm bg-rose-50 border-slate-200 hover:border-rose-500"
                      onClick={() => setRegRole('nurse')}
                      data-testid="reg-role-nurse"
                    >
                      <div className="w-10 h-10 rounded-lg bg-rose-50 flex items-center justify-center">
                        <Heart className="w-5 h-5 text-rose-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-slate-900">Nurse</p>
                        <p className="text-xs text-slate-500">Connect with patients</p>
                      </div>
                    </button>
                  </div>
                ) : (
                  /* Registration Form */
                  <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                    <button type="button" onClick={() => { setRegRole(''); setRegCategory(''); }} className="flex items-center text-sm text-slate-500 hover:text-slate-700">
                      <ArrowLeft className="w-4 h-4 mr-1" />Change Role
                    </button>

                    <div className={`flex items-center gap-2 p-2.5 rounded-lg ${roleStyleMap[regRole]?.bg}`}>
                      {(() => { const R = roleStyleMap[regRole]; return R ? <R.icon className={`w-4 h-4 ${R.color}`} /> : null; })()}
                      <span className="text-sm font-medium text-slate-700 capitalize">Registering as {regRole}</span>
                    </div>

                    <div>
                      <Label>Full Name *</Label>
                      <div className="relative mt-1">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input type="text" placeholder="Enter your full name" value={regName} onChange={(e) => setRegName(e.target.value)} className="pl-10" data-testid="register-name" required />
                      </div>
                    </div>

                    <div>
                      <Label>Phone Number *</Label>
                      <div className="flex gap-2 mt-1">
                        <div className="w-16 h-10 px-2 border border-slate-200 rounded-lg bg-slate-50 flex items-center justify-center text-sm font-medium text-slate-700">+91</div>
                        <Input type="tel" placeholder="10-digit phone number" value={regPhone} onChange={(e) => setRegPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} className={`flex-1 ${regPhone && !validatePhone(regPhone) ? 'border-red-400' : ''}`} maxLength={10} data-testid="register-phone" required />
                      </div>
                      {regPhone && !validatePhone(regPhone) && (
                        <p className="text-xs text-red-500 mt-1">Valid 10-digit number enter karein (6/7/8/9 se start)</p>
                      )}
                    </div>

                    {(regRole === 'doctor' || regRole === 'nurse') && (
                      <div>
                        <Label>Email *</Label>
                        <div className="relative mt-1">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input type="email" placeholder="email@example.com" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} className="pl-10" data-testid="register-email" required />
                        </div>
                      </div>
                    )}

                    <div>
                      <Label>Password *</Label>
                      <div className="relative mt-1">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input type={showPassword ? 'text' : 'password'} placeholder="Minimum 6 characters" value={regPassword} onChange={(e) => setRegPassword(e.target.value)} className="pl-10 pr-10" data-testid="register-password" required minLength={6} />
                        <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {regPassword && regPassword.length < 6 && (
                        <p className="text-xs text-red-500 mt-1">Minimum 6 characters required</p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      className="w-full h-11 bg-[#0F62FE] hover:bg-[#0050E6] disabled:bg-slate-300 disabled:cursor-not-allowed"
                      disabled={loading || !regName || !validatePhone(regPhone) || regPassword.length < 6 || ((regRole === 'doctor' || regRole === 'nurse') && !regEmail)}
                      data-testid="register-btn"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                      Create {regRole === 'doctor' ? 'Doctor' : regRole === 'nurse' ? 'Nurse' : regRole === 'clinic' ? 'Clinic' : 'Patient'} Account
                    </Button>

                    <p className="text-center text-sm text-slate-500 mt-2">
                      Already have an account?{' '}
                      <button type="button" className="text-[#0F62FE] hover:underline font-medium" onClick={() => setActiveTab('login')}>Login</button>
                    </p>
                  </form>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
